// Pure data logic for the public player profile "Attributes" tab.
//
// Input contract (backend, /dashboard/players/public/<id>/ability/):
//   { updated_at: int|null, comprehensive: int|null,
//     ability: [[type_id, value], ...],
//     ability_detail: [[category_id, [[detail_id, value], ...]], ...] }
// Defensive: rows may carry a legacy 3rd element (ignored), fields may be
// missing/null, values may fall outside 0-100 (kept raw here; the radar
// geometry clamps for drawing only).

// TheSports main ability types (type_id -> key).
export const MAIN_TYPES = {
  1: "diving",
  2: "anticipation",
  3: "control",
  4: "aerial",
  5: "mentality",
  6: "attacking",
  7: "defending",
  8: "creativity",
  9: "habilidad",
  10: "pace",
  11: "physical",
  12: "dribbling",
  13: "passing",
  14: "shooting",
  15: "jumping",
  16: "gkdiving",
  17: "gkhandling",
  18: "gkkicking",
  19: "gkpositioning",
  20: "gkreflexes",
  21: "goalkeeping",
  22: "movement",
};

// Detail categories (category_id -> ordered detail keys; detail_id is the
// 1-based index into this order).
export const DETAIL_CATEGORIES = {
  5: [
    "aggression",
    "interceptions",
    "positioning",
    "vision",
    "penalties",
    "composure",
  ],
  6: ["crossing", "finishing", "heading", "shortpass", "volleys"],
  7: ["marking", "standing", "sliding"],
  9: ["dribbling", "curve", "fkacc", "longpass", "ballcontrol"],
  11: ["shotpower", "jumping", "stamina", "strength", "longshots"],
  21: ["gkdiving", "gkhandling", "gkkicking", "gkpositioning", "gkreflexes"],
  22: ["accel", "sprint", "agility", "reactions", "balance"],
};

// Mains that are parents of a detail category (rule B: never rendered as
// bars — only overview axis + card header value).
export const PARENT_IDS = [5, 6, 7, 9, 11, 21, 22];

// Fixed field-player overview axis order.
export const FIELD_OVERVIEW_ORDER = [5, 6, 7, 9, 11, 22];

// Fixed field card order (category ids); the combined physical+defending
// card always comes last, goalkeeping card (gk variant) always first.
export const FIELD_CARD_ORDER = [6, 9, 22, 5];

const GK_MAIN_IDS = [16, 17, 18, 19, 20];

const toFinite = (v) => {
  if (v === null || v === undefined || v === "" || typeof v === "boolean") {
    return null;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// ability: [[type_id, value(, ignored)], ...] -> Map(type_id -> raw value)
function parseMains(payload) {
  const out = new Map();
  const rows = Array.isArray(payload && payload.ability) ? payload.ability : [];
  for (const row of rows) {
    if (!Array.isArray(row) || row.length < 2) continue;
    const id = toFinite(row[0]);
    const value = toFinite(row[1]);
    if (id === null || value === null) continue;
    if (!MAIN_TYPES[id]) continue;
    out.set(id, value);
  }
  return out;
}

// ability_detail -> Map(category_id -> Map(detail_id -> raw value))
function parseDetails(payload) {
  const out = new Map();
  const cats = Array.isArray(payload && payload.ability_detail)
    ? payload.ability_detail
    : [];
  for (const cat of cats) {
    if (!Array.isArray(cat) || cat.length < 2) continue;
    const catId = toFinite(cat[0]);
    const keys = catId === null ? undefined : DETAIL_CATEGORIES[catId];
    if (!keys || !Array.isArray(cat[1])) continue;
    const values = new Map();
    for (const row of cat[1]) {
      if (!Array.isArray(row) || row.length < 2) continue;
      const detailId = toFinite(row[0]);
      const value = toFinite(row[1]);
      if (detailId === null || value === null) continue;
      if (detailId < 1 || detailId > keys.length) continue;
      values.set(detailId, value);
    }
    if (values.size > 0) out.set(catId, values);
  }
  return out;
}

function meanRounded(valueMap) {
  let sum = 0;
  let count = 0;
  valueMap.forEach((v) => {
    sum += v;
    count += 1;
  });
  return count > 0 ? Math.round(sum / count) : null;
}

// Parent header/axis value for a category: main value if present, otherwise
// the arithmetic mean of the category's details rounded to nearest int
// (flagged derived). null when neither exists.
function parentValue(catId, mains, details) {
  if (mains.has(catId)) {
    return { value: mains.get(catId), derived: false };
  }
  if (details.has(catId)) {
    return { value: meanRounded(details.get(catId)), derived: true };
  }
  return null;
}

function detailAxes(catId, details) {
  const keys = DETAIL_CATEGORIES[catId];
  const values = details.get(catId);
  const axes = [];
  if (!keys || !values) return axes;
  keys.forEach((key, index) => {
    const detailId = index + 1;
    if (values.has(detailId)) axes.push({ key, value: values.get(detailId) });
  });
  return axes;
}

function buildCategoryCard(catId, mains, details) {
  if (!details.has(catId)) return null; // mains alone never create a card
  const parent = parentValue(catId, mains, details);
  return {
    key: MAIN_TYPES[catId],
    axes: detailAxes(catId, details),
    parents: parent
      ? [{ key: MAIN_TYPES[catId], value: parent.value, derived: parent.derived }]
      : [],
  };
}

// Combined physical(11) + defending(7) card. Axes: physical details first,
// then defending details (only the present part(s)). Parents listed
// separately — the two are NEVER blended into one number.
function buildCombinedCard(mains, details) {
  if (!details.has(11) && !details.has(7)) return null;
  const parts = [];
  const axes = [];
  const parents = [];
  for (const catId of [11, 7]) {
    if (!details.has(catId)) continue;
    parts.push(MAIN_TYPES[catId]);
    axes.push(...detailAxes(catId, details));
    const parent = parentValue(catId, mains, details);
    if (parent) {
      parents.push({
        key: MAIN_TYPES[catId],
        value: parent.value,
        derived: parent.derived,
      });
    }
  }
  return { key: "combined", parts, axes, parents };
}

export function buildAbilityModel(payload, { primaryPosition } = {}) {
  const mains = parseMains(payload);
  const details = parseDetails(payload);

  // GK detection: goalkeeping details or any gk-specific main decide alone;
  // primaryPosition only breaks the tie in the ambiguous case (bare
  // goalkeeping parent main without gk details/mains). Main 1 diving alone
  // on a field player NEVER triggers gk.
  const hasGkDetail = details.has(21);
  const hasGkMains = GK_MAIN_IDS.some((id) => mains.has(id));
  let variant = "field";
  if (hasGkDetail || hasGkMains) {
    variant = "gk";
  } else if (mains.has(21) && primaryPosition === "GK") {
    variant = "gk";
  }

  // Rule A — name twins suppressed entirely.
  const dropped = new Set();
  if (details.has(9)) dropped.add(12); // habilidad detail beats main dribbling
  if (details.has(11)) dropped.add(15); // physical detail beats main jumping
  if (details.has(21)) GK_MAIN_IDS.forEach((id) => dropped.add(id));

  const dedupedMainIds = [];
  mains.forEach((_, id) => {
    if (!dropped.has(id)) dedupedMainIds.push(id);
  });

  // Sufficiency gate.
  const sufficient = details.size >= 2 || dedupedMainIds.length >= 5;

  // Overview axes.
  const overviewAxes = [];
  if (variant === "gk") {
    if (hasGkDetail) {
      overviewAxes.push(
        ...detailAxes(21, details).map((a) => ({ ...a, derived: false })),
      );
    } else if (hasGkMains) {
      for (const id of GK_MAIN_IDS) {
        if (mains.has(id)) {
          overviewAxes.push({
            key: MAIN_TYPES[id],
            value: mains.get(id),
            derived: false,
          });
        }
      }
    }
  } else {
    for (const catId of FIELD_OVERVIEW_ORDER) {
      const parent = parentValue(catId, mains, details);
      if (parent) {
        overviewAxes.push({
          key: MAIN_TYPES[catId],
          value: parent.value,
          derived: parent.derived,
        });
      }
    }
  }

  // Cards: fixed order attacking, habilidad, movement, mentality,
  // combined(physical+defending); gk variant puts the goalkeeping card first.
  const cards = [];
  if (variant === "gk") {
    const gkCard = buildCategoryCard(21, mains, details);
    if (gkCard) cards.push(gkCard);
  }
  for (const catId of FIELD_CARD_ORDER) {
    const card = buildCategoryCard(catId, mains, details);
    if (card) cards.push(card);
  }
  const combined = buildCombinedCard(mains, details);
  if (combined) cards.push(combined);

  // Rule C — standalone mains render once as further-rating rows.
  const further = [];
  const standaloneIds = dedupedMainIds
    .filter((id) => !PARENT_IDS.includes(id))
    .filter((id) => !GK_MAIN_IDS.includes(id)) // gk mains are overview axes, never bars
    .filter((id) => !(id === 1 && variant === "gk")) // diving covered by gkdiving on gk
    .sort((a, b) => a - b);
  for (const id of standaloneIds) {
    further.push({ key: MAIN_TYPES[id], value: mains.get(id) });
  }

  const hasDerived =
    overviewAxes.some((a) => a.derived) ||
    cards.some((card) => card.parents.some((p) => p.derived));

  return {
    sufficient,
    variant,
    comprehensive: toFinite(payload && payload.comprehensive),
    updatedAt: toFinite(payload && payload.updated_at),
    overviewAxes,
    cards,
    further,
    hasDerived,
  };
}

// Value band for coloring numbers/bars. Uses the RAW value.
export function band(v) {
  const n = Number(v);
  if (Number.isFinite(n)) {
    if (n >= 85) return { color: "#0F172A", i: 3 };
    if (n >= 75) return { color: "#0B5FCB", i: 2 };
    if (n >= 60) return { color: "#2183FE", i: 1 };
  }
  return { color: "#64748B", i: 0 };
}
